import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { friendsApi } from '../api/apiService';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { User, FriendInvitation, FriendRequest } from '../types/models';
import LevelDisplay from '../components/LevelDisplay';

interface FriendRequestWithFrom extends FriendRequest {
  fromKid: User;
}

interface SentRequestWithTo extends FriendRequest {
  toKid: User;
}

const FriendsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { userType } = useAuth();
  const { user } = useUser();

  const [friends, setFriends] = useState<User[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendRequestWithFrom[]>([]);
  const [sentRequests, setSentRequests] = useState<SentRequestWithTo[]>([]);
  const [activeInvitations, setActiveInvitations] = useState<FriendInvitation[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [friendToDelete, setFriendToDelete] = useState<User | null>(null);

  const kidId = userId || '';

  const loadData = useCallback(async () => {
    try {
      const [friendsRes, requestsRes, sentRes, invitationsRes] = await Promise.all([
        friendsApi.getFriends(kidId),
        friendsApi.getPendingRequests(kidId),
        friendsApi.getSentRequests(kidId),
        friendsApi.getActiveInvitations(kidId)
      ]);
      setFriends(friendsRes.data.friends || []);
      setPendingRequests(requestsRes.data || []);
      setSentRequests(sentRes.data || []);
      setActiveInvitations(invitationsRes.data || []);
    } catch (error) {
      console.error('Failed to load friends data:', error);
      setSnackbar({ open: true, message: 'Failed to load friends data', severity: 'error' });
    }
  }, [kidId]);

  useEffect(() => {
    if (kidId) {
      loadData();
    }
  }, [kidId, loadData]);

  const handleCreateInvitation = async () => {
    try {
      const response = await friendsApi.createInvitation(kidId);
      setActiveInvitations([response.data.invitation, ...activeInvitations]);
      setSnackbar({ open: true, message: 'Invitation link created!', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to create invitation', severity: 'error' });
    }
  };

  const handleCopyLink = (inviteCode: string) => {
    const link = `${window.location.origin}/invite/${inviteCode}`;
    navigator.clipboard.writeText(link);
    setSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
  };

  const handleConfirmRequest = async (requestId: string) => {
    try {
      await friendsApi.confirmRequest(requestId, kidId);
      setSnackbar({ open: true, message: 'Friend request accepted!', severity: 'success' });
      loadData();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to accept request', severity: 'error' });
    }
  };

  const handleDeclineRequest = async (requestId: string) => {
    try {
      await friendsApi.declineRequest(requestId, kidId);
      setSnackbar({ open: true, message: 'Friend request declined', severity: 'success' });
      loadData();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to decline request', severity: 'error' });
    }
  };

  const handleRemoveFriend = async () => {
    if (!friendToDelete) return;
    try {
      await friendsApi.removeFriend(kidId, friendToDelete.id!);
      setSnackbar({ open: true, message: 'Friend removed', severity: 'success' });
      setDeleteDialogOpen(false);
      setFriendToDelete(null);
      loadData();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to remove friend', severity: 'error' });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          My Friends
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleCreateInvitation}
        >
          Invite Friend
        </Button>
      </Box>

      {/* Active Invitation Links */}
      {activeInvitations.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon /> Share Your Invite Link
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Share this link with friends. They can use it to send you a friend request within 7 days.
            </Alert>
            {activeInvitations.map((invitation) => (
              <Box
                key={invitation.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  mb: 1
                }}
              >
                <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace' }}>
                  {window.location.origin}/invite/{invitation.inviteCode}
                </Typography>
                <Chip
                  label={`${Math.ceil((new Date(invitation.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days left`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Tooltip title="Copy link">
                  <IconButton onClick={() => handleCopyLink(invitation.inviteCode!)}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Pending Friend Requests */}
      {pendingRequests.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon /> Pending Requests ({pendingRequests.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {pendingRequests.map((request) => (
              <Box
                key={request.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  mb: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {request.fromKid.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {request.fromKid.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {request.fromKid.totalPoints} points
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckIcon />}
                    onClick={() => handleConfirmRequest(request.id!)}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={() => handleDeclineRequest(request.id!)}
                  >
                    Decline
                  </Button>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Sent Friend Requests */}
      {sentRequests.filter(r => r.status === 'PENDING').length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon /> Sent Requests
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {sentRequests.filter(r => r.status === 'PENDING').map((request) => (
              <Box
                key={request.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  mb: 1
                }}
              >
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  {request.toKid.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {request.toKid.name}
                  </Typography>
                  <Chip label="Waiting for response" size="small" color="warning" variant="outlined" />
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Friends List */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon /> My Friends ({friends.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {friends.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <PeopleIcon sx={{ fontSize: 60, opacity: 0.3, mb: 2 }} />
              <Typography variant="body1">No friends yet</Typography>
              <Typography variant="body2">Create an invite link and share it with your friends!</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {friends.map((friend) => (
                <Grid item xs={12} sm={6} md={4} key={friend.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      position: 'relative',
                      '&:hover': { boxShadow: 2 }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: 'primary.main',
                          mx: 'auto',
                          mb: 1
                        }}
                      >
                        {friend.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {friend.name}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LevelDisplay level={friend.level ?? 0} size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {friend.totalPoints} points
                      </Typography>
                      <Typography variant="body2" color="warning.main" fontWeight={600}>
                        {friend.coins} coins
                      </Typography>
                      {userType === 'parent' && (
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                          onClick={() => {
                            setFriendToDelete(friend);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Friend</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {friendToDelete?.name} from your friends list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRemoveFriend} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FriendsPage;